import { v4 as uuidv4 } from 'uuid';
import { Api } from '$api';
import { Utm } from '$shared/constants';
import { getStorageItem, setStorageItem } from '$utils/asyncStorage';

export const isValidUtm = (utm: Utm | null): boolean => {
  return Boolean(utm && utm.campaign);
};

// Stable per-device visitor id. May return '' when storage is blocked —
// the backend then dedups visits by ip instead.
export const ensureVisitorId = (): string => {
  const existing = getStorageItem('visitor_id');
  if (existing) {
    return existing;
  }

  const generated = uuidv4();
  setStorageItem('visitor_id', generated);
  return getStorageItem('visitor_id') || '';
};

// Публичный токен промо-ссылки: "<slug>-<rand6>", приходит в ?utm_slug=.
// Slug — строчные буквы и цифры без дефисов, поэтому в токене ровно один дефис.
const PROMO_LINK_TOKEN_REGEX = /^[a-z0-9]{2,32}-[0-9A-Za-z]{6}$/;

export const isPromoLinkToken = (value?: string | null): boolean =>
  Boolean(value && PROMO_LINK_TOKEN_REGEX.test(value));

// Фиксирует переход и возвращает владельца метки: по одному токену фронт не может
// знать, чей он, а для реферальной атрибуции нужен utm_source=user_<ownerId>.
export const postPromoLinkVisit = async (
  token: string,
  visitorId?: string,
): Promise<{ ownerId: number; campaign: string } | null> => {
  try {
    const data: any = await Api.post('/promo/links/visit', {
      utmSlug: token,
      visitorId,
      isMiniApp: Boolean(window.isWebApp),
    });

    return data?.ownerId ? { ownerId: data.ownerId, campaign: data.campaign } : null;
  } catch (e) {
    console.error('Failed to post promo link visit:', e);
    return null;
  }
};

const UTM_LOCAL_KEY = 'utm';

export const setUtmLocal = (utm: Utm): void => {
  try {
    localStorage.setItem(UTM_LOCAL_KEY, JSON.stringify(utm));
  } catch (e) {
    console.error('Failed to set UTM in localStorage:', e);
  }
};

export const getUtmLocal = (): Utm | null => {
  try {
    const data = localStorage.getItem(UTM_LOCAL_KEY);
    if (!data) {
      return null;
    }

    const utm: Utm | null = JSON.parse(data);
    if (!utm) {
      return null;
    }

    return isValidUtm(utm) ? { ...utm, type: 'local' } : null;
  } catch (e) {
    console.error('Failed to get UTM from localStorage:', e);
    return null;
  }
};

export const setUtmServer = async (utm: Utm): Promise<void> => {
  try {
    await Api.post('/registration/utm', {
      id: utm.id,
      source: utm.source,
      medium: utm.medium,
      utm: utm.campaign,
      content: utm.content,
      term: utm.term,
      visitorId: utm.visitorId,
      extraParam1: utm.extraParam1,
      extraParam2: utm.extraParam2,
      extraParam3: utm.extraParam3,
    });
  } catch (e) {
    console.error('Failed to set UTM on server:', e);
  }
};

export const getUtmServer = async (): Promise<Utm | null> => {
  try {
    const data: any = await Api.get('/utm/detect');
    const utm: Utm = {
      id: data.id || undefined,
      source: data.utm_source || undefined,
      medium: data.utm_medium || undefined,
      campaign: data.utm_campaign || undefined,
      content: data.utm_content || undefined,
      term: data.utm_term || undefined,
      visitorId: data.visitor_id || undefined,
      extraParam1: data.extraParam1 || undefined,
      extraParam2: data.extraParam2 || undefined,
      extraParam3: data.extraParam3 || undefined,
    };
    return isValidUtm(utm) ? { ...utm, type: 'remote' } : null;
  } catch (e) {
    console.error('Failed to get UTM from server:', e);
    return null;
  }
};

export const getUtmFromUrl = (url?: URL | URLSearchParams | string): Utm | null => {
  try {
    let params: URLSearchParams;

    if (url instanceof URLSearchParams) {
      params = url;
    } else if (url instanceof URL) {
      params = url.searchParams;
    } else if (typeof url === 'string') {
      params = new URL(url).searchParams;
    } else {
      params = new URL(window.location.href).searchParams;
    }

    const utm: Utm = {
      id: params.get('utm_id') || undefined,
      source: params.get('utm_source') || undefined,
      medium: params.get('utm_medium') || undefined,
      campaign: params.get('utm_campaign') || params.get('utm') || undefined,
      term: params.get('utm_term') || undefined,
      content: params.get('utm_content') || undefined,
      visitorId: params.get('visitor_id') || undefined,
      extraParam1: params.get('extra_param1') || undefined,
      extraParam2: params.get('extra_param2') || undefined,
      extraParam3: params.get('extra_param3') || undefined,
    };

    return isValidUtm(utm) ? { ...utm, type: 'url' } : null;
  } catch (e) {
    console.error('Failed to parse UTM from URL:', e);
    return null;
  }
};

export const getUtmOffline = (url?: URL | URLSearchParams | string): Utm | null => {
  const first = getUtmFromUrl(url);
  if (first) {
    return first;
  }

  const second = getUtmLocal();
  if (second) {
    return second;
  }

  return null;
};

export const getUtmOnline = async (
  url?: URL | URLSearchParams | string,
): Promise<Utm | null> => {
  const first = getUtmOffline(url);
  if (first) {
    return first;
  }

  const second = await getUtmServer();
  if (second) {
    return second;
  }

  return null;
};
